import React from "react";
import DashboardCards from "../components/Blocks/DashboardCards";
import { usePatient } from "../context/PatientContext";
import MainAreaWrapper from "../layout/MainAreaWrapper";
import { MenuItem, Select } from "@mui/material";
import * as API from "../HTTPS/ReportApi"
import { useQuery } from "@tanstack/react-query";
import VitalChartsComponent from "../components/Charts/VitalReportChart";

const POSSIBLE_EVENTS = [
    "Poor Night of Sleep",
    "Seizure",
    "Other",
]

const HELPER_TEXT = {
    "Poor Night of Sleep": "For poor nights of sleep, we recommend paying particular attention to diet, etc.",
    "Seizure": "For seizures, we recommend paying....",
    "Other": "",
}

const QUICK_DAYS = [1, 2, 3, 5, 7, 14];

const RootCauseAnalysis = () => {
  const { patient } = usePatient();
  const [event, setEvent] = React.useState("");
  const [otherText, setOtherText] = React.useState("");
  const [daysBack, setDaysBack] = React.useState(3);
  const now = new Date();
  const [date, setDate] = React.useState(new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16));
  const { data: rootCauseReport, isLoading } = useQuery({
    queryKey: ["root-cause-report", patient?.id, date, daysBack],
    queryFn: () =>
      API.fetchRootCauseReport(
        patient?.id,
        date,
        daysBack
      ),
    enabled: !!patient?.id && !!date,
    // refetchOnWindowFocus: false,
    // staleTime: 5 * 1000,
  });

  return (
    <MainAreaWrapper>
      <main className="mt-28 lg:mt-10 bg-gradient-to-b from-[#FAFCFEBF]/70 via-[#FAFCFEBF]/50 to-transparent rounded-t-3xl">
        {/* <Breadcrumb /> */}
        <div className="mx-auto p-2 md:p-6 2xl:p-10">
          {/* <!-- ===== Content Area Start ===== --> */}
          <div className="mb-6 px-2 py-3">
            {/* <UserProfileMenuBar /> */}

            <div className="">
              <span className="text-[#1D3E7D] text-4xl font-bold">
                Root Cause Analysis
              </span>
            </div>
          </div>
           <div className="bg-white rounded-xl p-6 max-w-2xl ml-4 shadow-lg">

        {/* Compact Form Grid */}
        <div  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Event Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event:
            </label>
            <select
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {POSSIBLE_EVENTS.map((event) => (
                <option key={event} value={event}>
                  {event}
                </option>
              ))}
            </select>
          </div>

          {/* Date Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Time:
            </label>
            <input
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Other Event Input */}
        {event === 'Other' && (
          <div className="mb-4">
            <input
              placeholder="Please specify event type"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Days Back - Horizontal Layout */}
        <div className="flex items-center flex-wrap gap-2 mb-4 text-sm text-gray-700">
          <span>Within</span>
          <input
            type="number"
            value={daysBack}
            onChange={(e) => setDaysBack(e.target.value)}
            min="1"
            className="w-12 p-1 px-2 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <span>days of event</span>
          
          {/* Quick Select Chips */}
          <div className="flex gap-1 ml-2">
            {QUICK_DAYS.map((days) => (
              <button
                key={days}
                onClick={() => setDaysBack(days)}
                className={`px-2 py-0.5 rounded-full text-xs cursor-pointer transition-all ${
                  daysBack === days
                    ? 'border border-blue-500 bg-blue-50 text-blue-600'
                    : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {days}
              </button>
            ))}
          </div>
        </div>

        {/* Helper Text */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 leading-relaxed">
          <strong>Tip:</strong> {HELPER_TEXT[event]}
        </div>
        </div>

          <div className="flex flex-wrap gap-5 items-center justify-center sm:justify-start">
            {
              isLoading 
              ? <div className="flex space-x-2 mx-auto mt-16">
                <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
                <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div> 
            : rootCauseReport 
            ? <VitalChartsComponent 
                annotations={rootCauseReport.annotations} 
                baselines={rootCauseReport.baselines} 
                vitalData={rootCauseReport.vitalData} 
                eventTime={date} 
              /> 
            : null
            }
          </div>
        </div>
      </main>
    </MainAreaWrapper >
  );
};

export default RootCauseAnalysis;
